// -----------------------------------------------------------------------------
// "Manage members" modal — add or remove people from a named channel. Only
// available for channels (DMs are 2-person and member edits don't apply).
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/solid";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../../layouts/Modal";
import classNames from "../../../utils/classNames";
import { fullNameFor } from "../../../utils/chatHelpers";
import { IChatChannel, IChatUser } from "../../../models/Chat";
import ChatAvatar from "../ChatAvatar";

interface ManageMembersModalProps {
  open: boolean;
  channel: IChatChannel | null;
  me: IChatUser | null;
  users: IChatUser[];
  busy: boolean;
  onClose: () => void;
  onAdd: (channelId: string, userIds: string[]) => void;
  onRemove: (channelId: string, userId: string) => void;
}

export default function ManageMembersModal({
  open,
  channel,
  me,
  users,
  busy,
  onClose,
  onAdd,
  onRemove,
}: ManageMembersModalProps) {
  const [toAdd, setToAdd] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) {
      setToAdd([]);
      setSearch("");
    }
  }, [open, channel?.id]);

  if (!channel) return null;

  const memberIdSet = new Set(channel.memberIds);
  const currentMembers = channel.memberIds
    .map((id) => users.find((u) => u.id === id))
    .filter((u): u is IChatUser => !!u);

  const term = search.trim().toLowerCase();
  const candidates = users.filter((u) => {
    if (memberIdSet.has(u.id)) return false;
    if (!term) return true;
    return `${u.forename} ${u.surname} ${u.role ?? ""}`
      .toLowerCase()
      .includes(term);
  });

  const toggleCandidate = (id: string) =>
    setToAdd((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const lastMember = currentMembers.length <= 1;

  return (
    <Modal open={open} setOpen={(v) => !v && onClose()} width="sm:max-w-lg">
      <div className="flex items-center justify-between pb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Manage members
          </h3>
          <p className="text-xs text-gray-500">
            Add or remove people from{" "}
            <span className="font-medium text-gray-700">#{channel.name}</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 hover:bg-gray-100"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Current members ({currentMembers.length})
          </p>
          <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-gray-200">
            {currentMembers.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-500">
                No members yet.
              </p>
            ) : (
              currentMembers.map((u) => {
                const isMe = me && u.id === me.id;
                return (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
                  >
                    <ChatAvatar user={u} size="sm" showPresence />
                    <span className="flex-1 text-sm">
                      <span className="block font-medium text-gray-900">
                        {fullNameFor(u)}
                        {isMe ? (
                          <span className="ml-1 text-xs font-normal text-gray-400">
                            (you)
                          </span>
                        ) : null}
                      </span>
                      {u.role ? (
                        <span className="block text-xs text-gray-500">
                          {u.role}
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      disabled={busy || lastMember}
                      onClick={() => onRemove(channel.id, u.id)}
                      title={
                        lastMember
                          ? "Cannot remove the last member"
                          : isMe
                          ? "Leave channel"
                          : "Remove from channel"
                      }
                      className={classNames(
                        "rounded-md border px-2 py-1 text-xs font-medium",
                        lastMember
                          ? "border-gray-200 bg-gray-50 text-gray-300"
                          : "border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
                      )}
                    >
                      {isMe ? "Leave" : "Remove"}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Add people
          </p>
          <label className="mt-1 flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teammates"
              className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
            />
          </label>
          <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200">
            {candidates.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-500">
                {term ? "No teammates match." : "Everyone is already a member."}
              </p>
            ) : (
              candidates.map((u) => {
                const checked = toAdd.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleCandidate(u.id)}
                    className={classNames(
                      "flex w-full items-center gap-3 border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-gray-50",
                      checked ? "bg-primary-50" : ""
                    )}
                  >
                    <ChatAvatar user={u} size="sm" />
                    <span className="flex-1 text-sm">
                      <span className="block font-medium text-gray-900">
                        {fullNameFor(u)}
                      </span>
                      {u.role ? (
                        <span className="block text-xs text-gray-500">
                          {u.role}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={classNames(
                        "flex h-5 w-5 items-center justify-center rounded border",
                        checked
                          ? "border-primary-600 bg-primary-600 text-white"
                          : "border-gray-300 bg-white"
                      )}
                    >
                      {checked ? <CheckIcon className="h-3 w-3" /> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Done
        </button>
        <button
          type="button"
          disabled={busy || toAdd.length === 0}
          onClick={() => {
            onAdd(channel.id, toAdd);
            setToAdd([]);
            setSearch("");
          }}
          className={classNames(
            "rounded-md px-3 py-1.5 text-sm font-semibold text-white",
            !busy && toAdd.length > 0
              ? "bg-primary-600 hover:bg-primary-500"
              : "bg-gray-300"
          )}
        >
          {busy
            ? "Saving…"
            : toAdd.length > 0
            ? `Add ${toAdd.length} ${toAdd.length === 1 ? "person" : "people"}`
            : "Add people"}
        </button>
      </div>
    </Modal>
  );
}
